import React, { useMemo, useState } from "react";
import { ColDef, ColGroupDef } from "ag-grid-community";
import moment from "moment";
import ReportPageShell from "../components/ReportPageShell";
import DetailTable from "../components/DetailTable";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { useDetailQuery } from "../shared/useDetailQuery";
import { useSearchGate } from "../shared/useSearchGate";
import { moneyCol, textCol } from "../shared/detailColumns";
import { COUNT_METRICS, MetricKey, WebCheckoutListResponse, WebCheckoutRow } from "../api/types";

const LIMIT_OPTIONS = [500, 2000, 10000, 50000];
const DEFAULT_LIMIT = 2000;

const TOOLTIP =
  "Web Checkout (RP-2182) — one row per legacy web-checkout order, matching the legacy admin-app Web " +
  "Checkout Report. Those orders migrated to the Invoice entity (not Order), so this reads Invoices of type " +
  "Web Checkout. Money columns are the migrated amounts (Subtotal/Service Fee/Total), rescaled for display " +
  "to undo a x100 migration overscale (see MIGRATED_MONEY_OVERSCALE). Filter by event/date, then export to CSV.";

// ---------------------------------------------------------------------------
// RP-2182 QA "Decimal is off" — DISPLAY-ONLY correction for a source double-scale.
//
// Legacy `web_checkout_orders`.amount / service_fee / total_amount are stored in CENTS: the legacy
// checkout app computes amountInCents / serviceFeeAmount / totalAmount in cents (web-checkout
// Checkout.tsx) and ronin-api writes them verbatim (webCheckout/web_checkout_order_create.js), which
// is why the legacy grid formats every money column as `value / 100` (reports/web-checkout helpers.tsx).
//
// InvoiceMigrator.MigrateWebCheckoutAsync nevertheless pushed those values through ToCents() (x100) —
// correct for the legacy `invoices` table, whose columns really are numeric DOLLARS, but wrong here. So
// Invoice.Subtotal / ServiceFee / GrandTotal on web-checkout invoices carry cents x 100, and the engine
// surfaces those fields verbatim (DetailListEngine.WebCheckoutListAsync: "RAW migrated Invoice fields").
// Everything on this report — row columns AND the response Totals — therefore arrives 100x too large,
// so centsToUsd's /100 lands the decimal two places right: a $51.25 order rendered as $5,125.00.
//
// The real fix belongs in the migrator + a data backfill. Until that lands, undo the overscale HERE, at
// the display boundary only. WHEN THE SOURCE DATA IS CORRECTED, DELETE this constant, `toTrueCents`,
// `descaleMigratedMoney` and the CSV legend note — otherwise this report will read 100x LOW.
// ---------------------------------------------------------------------------
const MIGRATED_MONEY_OVERSCALE = 100;

// Migrated value (cents x 100) -> true cents, the unit centsToUsd expects. Genuine rows are always an
// exact multiple of 100 (the migrator rounded cents x 100 to an int), so the rounding is a guard only.
const toTrueCents = (v?: number | null): number =>
  Math.round((v ?? 0) / MIGRATED_MONEY_OVERSCALE);

// Rescale every money field of a WebCheckoutList response — the three row columns and the money
// Totals behind the summary cards (counts pass through untouched). Applied once, before the grid, so
// the cells, the sort/filter values, the summary cards and the CSV export all read the same numbers.
export function descaleMigratedMoney(data: WebCheckoutListResponse): WebCheckoutListResponse {
  const rows = data.rows.map((r: WebCheckoutRow) => ({
    ...r,
    totalAmount: toTrueCents(r.totalAmount),
    subtotal: toTrueCents(r.subtotal),
    serviceFee: toTrueCents(r.serviceFee),
  }));

  const totals = Object.fromEntries(
    Object.entries(data.totals).map(([k, v]) => [
      k,
      COUNT_METRICS.has(k as MetricKey) ? v : toTrueCents(v),
    ])
  ) as WebCheckoutListResponse["totals"];

  return { ...data, rows, totals };
}

// Documents the deliberate legacy-parity decisions + the migration gaps, so QA reads them in the export.
const CSV_LEGEND = [
  "",
  "One row per legacy web-checkout order. Source: Invoices with InvoiceType = 'Web Checkout' (legacy web_checkout_orders migrated to Invoice, NOT Order).",
  "Money columns (Total Amount / Subtotal / Service Fee) are the migrated amounts (legacy total_amount / amount / service_fee). No Net/Gross reinterpretation is applied; web checkout carries no tax.",
  "SCALE NOTE: the legacy columns are stored in CENTS, but the Invoice migration ran them through a dollars-to-cents conversion (x100), so the stored Invoice amounts are 100x too large. This report divides by that 100 for DISPLAY ONLY (rows, summary totals and this CSV alike) so the figures match the legacy Web Checkout Report cent-for-cent. The underlying Invoice data is unchanged and still needs a migrator fix + backfill.",
  "Reconciliation: Subtotal + Service Fee = Total Amount (web checkout has no tax) holds on clean source data. A non-zero residual means an anomalous legacy row; it is shown as-is, not reconciled.",
  "Transaction Date = legacy created_at, displayed with the legacy report's hardcoded UTC-5 (EST) offset. The date filter buckets on that SAME -5h calendar day, so a row's displayed day always equals its filtered day (it does NOT use the 5 AM business-day window).",
  "GAP — Reference ID: legacy reference_id was NOT migrated (only processor_reference_id survived), so that column is always blank.",
  "Selection: the legacy report showed the 'selection' field only. The migrator merged 'selector' + 'selection' into one line-item description, so this column shows the merged 'selector: selection' string (the merge is lossy and cannot be un-merged).",
];

// The legacy grid displayed Transaction Date as moment.utc(created_at).subtract(5,'hours') in
// 'ddd, MMM DD, YYYY h:mm a'. Replicated here so the new report matches the legacy report cell-for-cell
// (a hardcoded EST offset — a legacy quirk carried for parity, disclosed in the CSV legend).
const legacyDateCol = (): ColDef => ({
  field: "transactionDateUtc",
  headerName: "Transaction Date",
  pinned: "left",
  width: 210,
  valueFormatter: (p) =>
    p.value ? moment.utc(p.value as string).subtract(5, "hours").format("ddd, MMM DD, YYYY h:mm a") : "",
});

// Columns in the EXACT legacy display order (helpers.tsx), then a few bonus scope columns appended.
function webCheckoutColumns(): (ColDef | ColGroupDef)[] {
  return [
    legacyDateCol(),
    textCol("referenceId", "Reference ID", { width: 150 }), // always blank — legacy reference_id not migrated
    textCol("processorReferenceId", "Processor Reference ID", { width: 190 }),
    // Legacy showed `selection` only; migrator merged selector+selection, so this is the merged string.
    textCol("selection", "Selection", { width: 220 }),
    moneyCol("totalAmount", "Total Amount"),
    moneyCol("subtotal", "Subtotal"),
    moneyCol("serviceFee", "Service Fee"),
    textCol("clientName", "Client Name", { width: 160 }),
    textCol("clientEmail", "Client Email", { width: 200 }),
    textCol("clientPhone", "Client Phone", { width: 140 }),
    textCol("billingName", "Billing Name", { width: 160 }),
    textCol("billingAddress", "Billing Address", { width: 280 }),
    textCol("cardType", "Card Type", { width: 110 }),
    textCol("cardLastFour", "Last 4 of Card", { width: 120 }),
    // --- bonus scope columns (not in the legacy report) ---
    textCol("eventName", "Event", { width: 160 }),
    textCol("vendorId", "Vendor Id", { width: 150 }),
    textCol("legacyId", "Legacy ID", { width: 110 }),
  ];
}

// Grid + summary cards over the rescaled response. Split out of the page so the rescale can be
// memoised on the response identity (it walks every row, and the grid can hold 50k of them).
function WebCheckoutTable({ data }: { data: WebCheckoutListResponse }) {
  const scaled = useMemo(() => descaleMigratedMoney(data), [data]);
  return (
    <DetailTable
      data={scaled}
      columnDefs={webCheckoutColumns()}
      summaryMetrics={["OrderCount", "Gross", "Surcharge", "TransactionAmount"]}
      emphasisMetric="TransactionAmount"
      exportName="WebCheckout"
      csvExtra={CSV_LEGEND}
    />
  );
}

// Web Checkout detail page (RP-2182) — WebCheckoutList.
export default function WebCheckoutReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters, limit });
  const state = useDetailQuery(
    submitted
      ? toQueryParams("WebCheckoutList", submitted.filters, { limit: submitted.limit })
      : null
  );

  return (
    <ReportPageShell
      title="Web Checkout"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      actions={
        <label className="flex items-center gap-1 text-sm text-gray-600">
          Row limit
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            {LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n.toLocaleString()}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {(data) => <WebCheckoutTable data={data as WebCheckoutListResponse} />}
    </ReportPageShell>
  );
}
